import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getSubscriptionStatusDialogConfig } from './subscriptionStatusDialogConfig';
import { useSubscriptionList } from '../../hooks/useSubscriptionList';
import { updateSubscriptionStatusApiService } from '../../api/updateSubscriptionStatusApiService';
import type { SubscriptionListItem } from '../../types/subscriptionListType';
import {
  formatPrice,
  formatDuration,
  getBillingCycleLabel,
  formatDate,
} from '../../hooks/useViewSubscriptionPlanContent';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';

export default function SubscriptionList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showCreatePlanSuccessSnackbar, setShowCreatePlanSuccessSnackbar] =
    useState(false);
  const [showEditPlanSuccessSnackbar, setShowEditPlanSuccessSnackbar] =
    useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const MIN_SPACE_BELOW_FOR_MENU = 70;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionListItem | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');

  const {
    subscriptions,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    billingCycleOptions,
    setBillingCycleOptions,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchSubscriptions,
  } = useSubscriptionList();

  useEffect(() => {
    if (location.state?.showCreatePlanSuccess) {
      setShowCreatePlanSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.showEditPlanSuccess) {
      setShowEditPlanSuccessSnackbar(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  // Close filter/row menu when user clicks "Plany" in side nav (same page)
  useEffect(() => {
    const handleClosePopups = () => {
      setIsFilterOpen(false);
      setOpenMenuRowId(null);
    };
    window.addEventListener(
      'subscription-list-close-popups',
      handleClosePopups
    );
    return () =>
      window.removeEventListener(
        'subscription-list-close-popups',
        handleClosePopups
      );
  }, []);

  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      const anchor = menuAnchorRef.current;
      if (anchor) {
        const firstMenuitem =
          anchor.querySelector<HTMLElement>('[role="menuitem"]');
        if (firstMenuitem) {
          requestAnimationFrame(() => firstMenuitem.focus());
        }
      }
    } else {
      const hadOpenMenu = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpenMenu && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const getModifiedByInitial = (name: string): string => {
    const trimmed = name.trim();
    const first = trimmed.charAt(0);
    return first ? first.toUpperCase() : '—';
  };

  const extractUserName = (
    value:
      | string
      | { name?: string; email?: string; [key: string]: unknown }
      | null
      | undefined
  ): string => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'string') {
      return value.trim() ? value : '—';
    }
    if (typeof value === 'object') {
      if (value.name && typeof value.name === 'string') {
        return value.name.trim() ? value.name : '—';
      }
      if (value.email && typeof value.email === 'string') {
        return value.email.trim() ? value.email : '—';
      }
    }
    return '—';
  };

  const columns: Column<SubscriptionListItem>[] = useMemo(
    () => [
      {
        key: 'planName',
        header: 'Imię i nazwisko',
        width: '184px',
        render: (row) => {
          return (
            <div className="flex items-center w-full min-w-0">
              <Tooltip content={row.planName}>
                <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                  {row.planName}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        key: 'planDescription',
        header: 'Opis',
        width: '300px',
        render: (row) => {
          return (
            <Tooltip content={row.planDescription || '—'}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.planDescription || '—'}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'price',
        header: 'Cena',
        width: '120px',
        render: (row) => {
          const formattedPrice = formatPrice(row.price);
          const displayPrice = formattedPrice ? `${formattedPrice} zł` : '—';
          return (
            <Tooltip content={displayPrice}>
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {displayPrice}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'billingCycle',
        header: 'Cykl rozliczeniowy ',
        width: '160px',
        render: (row) => {
          const billingCycleLabel = getBillingCycleLabel(row.billingCycle);
          return (
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {billingCycleLabel}
            </span>
          );
        },
      },
      {
        key: 'duration',
        header: 'Okres',
        width: '112px',
        render: (row) => {
          const formattedDuration = formatDuration(
            row.duration,
            row.durationType
          );
          return (
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {formattedDuration}
            </span>
          );
        },
      },
      {
        key: 'discount',
        header: 'Rabaty',
        width: '112px',
        render: () => {
          // Discount field doesn't exist in SubscriptionPlan type yet
          return (
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              —
            </span>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        width: '184px',
        render: (row, index) => (
          <div
            data-testid={`toggle-status-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPlan(row);
              setIsDialogOpen(true);
            }}
            className="cursor-pointer inline-flex"
          >
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton
                checked={row.status === 'active'}
                onChange={() => {}}
                label={row.status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                size="Small"
              />
            </div>
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: 'Data utworzenia',
        width: '192px',
        render: (row) => {
          const formattedDate = formatDate(row.createdAt);
          return (
            <Tooltip content={formattedDate}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {formattedDate}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'updatedBy',
        header: 'Zmodyfikowano przez',
        width: '224px',
        render: (row) => {
          const userName = extractUserName(row.updatedBy);
          if (!userName || userName === '—') {
            return (
              <span className="text-on-surface-variant text-sm leading-5 tracking-[0.25px]">
                —
              </span>
            );
          }
          const initial = getModifiedByInitial(userName);
          return (
            <div className="flex items-center w-full min-w-0 gap-2">
              <div
                className="w-8 h-8 rounded-full bg-primary-color-90 flex items-center justify-center shrink-0"
                aria-hidden
              >
                <span className="text-primary-color-30 text-base font-medium">
                  {initial}
                </span>
              </div>
              <Tooltip content={userName}>
                <span className="text-on-surface-variant text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                  {userName}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        key: 'updatedAt',
        header: 'Data modyfikacji ',
        width: '192px',
        render: (row) => {
          const formattedDate = formatDate(row.updatedAt);
          return (
            <Tooltip content={formattedDate}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {formattedDate}
              </span>
            </Tooltip>
          );
        },
      },
      {
        key: 'actions',
        header: 'Akcje',
        width: '60px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    const spaceBelow = visibleBottom - buttonRect.bottom;
                    setMenuOpensUpward(spaceBelow < MIN_SPACE_BELOW_FOR_MENU);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla planu ${row.planName}`}
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute left-1/3 z-50 -translate-x-1/2 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      return;
                    }
                    const anchor = menuAnchorRef.current;
                    if (!anchor) {
                      return;
                    }
                    const items =
                      anchor.querySelectorAll<HTMLElement>('[role="menuitem"]');
                    const lastItem = items[items.length - 1];
                    if (
                      lastItem &&
                      document.activeElement === lastItem &&
                      !e.shiftKey
                    ) {
                      setOpenMenuRowId(null);
                    }
                  }}
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: 'Podgląd',
                      },
                      {
                        id: 'EDIT',
                        label: 'Edytuj',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`/Plany/Podgląd-planu/${row.id}`);
                      } else if (itemId === 'EDIT') {
                        navigate(`/Plany/Edycja-planu/${row.id}`);
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate, setSelectedPlan, setIsDialogOpen]
  );

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = () => {
    setBillingCycleOptions(appliedFilters.billingCycleOptions);
    setStatusOptions(appliedFilters.statusOptions);
    setIsFilterOpen(false);
  };

  const handleDialogClose = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  const getStatusErrorMessage = (apiError: string | undefined): string => {
    const STATUS_ERROR_MESSAGES: Record<string, string> = {
      'Subscription plan is already active':
        'Plan subskrypcji jest już aktywny.',
      'Subscription plan is already inactive':
        'Plan subskrypcji jest już nieaktywny.',
      'Proszę podać prawidłowe dane wejściowe.':
        'Status planu został już zmieniony.',
    };
    const fallback = 'Wystąpił błąd podczas aktualizacji statusu.';
    if (!apiError) {
      return fallback;
    }
    return STATUS_ERROR_MESSAGES[apiError] ?? apiError ?? fallback;
  };

  // Handle status update confirmation
  const handleConfirmStatusUpdate = async () => {
    if (!selectedPlan) {
      return;
    }

    setIsSubmitting(true);
    const isActive = selectedPlan.status === 'inactive'; // Will activate if currently inactive

    try {
      const result =
        await updateSubscriptionStatusApiService.updateSubscriptionStatus({
          planId: selectedPlan.id,
          isActive,
        });

      if (result.success) {
        const message = isActive
          ? 'Plan subskrypcji został pomyślnie aktywowany.'
          : 'Plan subskrypcji został pomyślnie dezaktywowany.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedPlan(null);
        refetchSubscriptions();
      } else {
        setStatusErrorMessage(getStatusErrorMessage(result.error));
        setShowStatusErrorSnackbar(true);
        setIsDialogOpen(false);
        setSelectedPlan(null);
        refetchSubscriptions();
      }
    } catch (_error) {
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
      setIsDialogOpen(false);
      setSelectedPlan(null);
      refetchSubscriptions();
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    subscriptions.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !appliedFilters.billingCycleOptions.some((opt) => opt.checked) &&
    !appliedFilters.statusOptions.some((opt) => opt.checked);

  return (
    <>
      {/* Custom styles for status update success snackbar to match Figma */}
      <style>{`
        .status-update-success-snackbar {
          width: 432px !important;
          min-height: 48px !important;
          box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.15), 0px 4px 8px 3px rgba(0, 0, 0, 0.08) !important;
        }
        .status-update-success-snackbar > div {
          padding-left: 16px !important;
          padding-right: 48px !important;
          padding-top: 14px !important;
          padding-bottom: 14px !important;
          position: relative !important;
          min-height: 48px !important;
        }
        .status-update-success-snackbar p {
          color: #FFFFFF !important;
          font-family: 'Roboto', sans-serif !important;
          font-weight: 400 !important;
          font-size: 14px !important;
          line-height: 20px !important;
          letter-spacing: 0.25px !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }
        .status-update-success-snackbar > div > div:last-child {
          position: absolute !important;
          top: 50% !important;
          right: 16px !important;
          transform: translateY(-50%) !important;
        }
        .status-update-success-snackbar button[aria-label="Zamknij"] {
          color: #FFFFFF !important;
        }
        .status-update-success-snackbar button[aria-label="Zamknij"]:hover {
          background-color: rgba(24, 26, 27, 0.08) !important;
        }
      `}</style>
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        <div
          className="flex items-center justify-between shrink-0"
          id="subscription-list-content"
        >
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            Plany
          </div>
          {!hasNoData && (
            <button
              onClick={() => navigate('/Plany/Utwórz-plan')}
              className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
              aria-label="Utwórz plan subskrypcji"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                Utwórz plan
              </span>
            </button>
          )}
        </div>

        {!hasNoData && (
          <>
            {/* Search and Filter */}
            <div className="flex items-center justify-between shrink-0">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Szukaj po nazwie lub cenie"
              />
              <div className="flex items-center gap-2">
                <div className={`dropdown ${isFilterOpen ? 'show' : ''}`}>
                  <style>{`
                .dropdown {
                  position: relative;
                }

                .dropdown-content {
                  position: absolute;
                  background-color: #f9f9f9;
                  min-width: 130px;
                  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
                  padding: 12px 16px;
                }

                .dropdown.show .dropdown-content {
                  display: block;
                }
              `}</style>

                  <FilterBar
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    isOpen={isFilterOpen}
                    filterCount={
                      appliedFilters.billingCycleOptions.filter(
                        (opt) => opt.checked
                      ).length +
                      appliedFilters.statusOptions.filter((opt) => opt.checked)
                        .length
                    }
                  />
                  <FilterPopup
                    isOpen={isFilterOpen}
                    onClose={handleCloseFilter}
                    billingCycleOptions={billingCycleOptions}
                    onBillingCycleChange={setBillingCycleOptions}
                    statusOptions={statusOptions}
                    onStatusChange={setStatusOptions}
                    onApply={handleApplyFiltersAndClose}
                    onClear={handleClearFilters}
                  />
                </div>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-lg border border-[#DCDDE0] px-[8px] py-[8px] cursor-pointer relative overflow-visible transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                  aria-label="Kolumny"
                >
                  <ViewColumnsIcon
                    className="h-6 w-6 text-on-surface-variant shrink-0"
                    aria-hidden
                  />
                </button>
              </div>
            </div>
          </>
        )}

        <div
          ref={tableScrollContainerRef}
          className="flex-1 min-h-0 flex flex-col overflow-x-auto overflow-y-auto"
        >
          {subscriptions.length === 0 && !loading && !isSearchClearing ? (
            hasNoData ? (
              <div className="flex flex-col items-center justify-center w-full h-full">
                <LottieAnimation
                  animationData={noDataAnimation}
                  width={277}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-start justify-start gap-1">
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                      Brak planów subskrypcji.
                    </div>
                    <div className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                      Utwórz plan subskrypcji, aby określić ceny, cykle
                      rozliczeniowe i limity użytkowania.
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/Plany/Utwórz-plan')}
                    className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center"
                  >
                    <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                      Utwórz plan
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* No Results State - filters/search applied but no results */
              <div className="flex flex-col items-center justify-center w-full h-full gap-3">
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
                  <div className="w-full h-6 text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    Nie znaleziono wyników.
                  </div>
                  <div className="w-full h-5 text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                    {search.trim() &&
                    (appliedFilters.billingCycleOptions.some(
                      (opt) => opt.checked
                    ) ||
                      appliedFilters.statusOptions.some((opt) => opt.checked))
                      ? 'Spróbuj zmienić kryteria wyszukiwania lub filtry.'
                      : search.trim()
                        ? 'Spróbuj zmienić kryteria wyszukiwania.'
                        : 'Spróbuj zmienić ustawienia filtrów.'}
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Table Content */
            <Table
              columns={columns}
              data={subscriptions}
              loading={loading}
              loadingRows={limit}
              stickyHeader
              getRowKey={(row) => row.id}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
              }
            />
          )}
        </div>

        {/* Pagination - Fixed at bottom */}
        {!hasNoData && totalCount > 10 && (
          <div className="shrink-0 px-4 -mx-4">
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </div>
            )}
          </div>
        )}

        {/* Create Plan Success Snackbar */}
        <Snackbar
          open={showCreatePlanSuccessSnackbar}
          message="Plan subskrypcji został pomyślnie utworzony."
          variant="success"
          onClose={() => setShowCreatePlanSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Edit Plan Success Snackbar */}
        <Snackbar
          open={showEditPlanSuccessSnackbar}
          message="Plan subskrypcji został pomyślnie zaktualizowany."
          variant="success"
          onClose={() => setShowEditPlanSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Status Update Success Snackbar - Custom styling to match Figma specs */}
        <Snackbar
          open={showStatusSuccessSnackbar}
          message={statusSuccessMessage}
          variant="success"
          onClose={() => setShowStatusSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
          className="status-update-success-snackbar"
        />

        {/* Status Update Error Snackbar */}
        <Snackbar
          open={showStatusErrorSnackbar}
          message={statusErrorMessage}
          variant="error"
          onClose={() => setShowStatusErrorSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton={true}
        />

        {/* Activate/Deactivate Plan Dialog */}
        {selectedPlan && (
          <UpdateStatusDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleConfirmStatusUpdate}
            isSubmitting={isSubmitting}
            {...getSubscriptionStatusDialogConfig(
              selectedPlan.planName,
              selectedPlan.status === 'active'
            )}
          />
        )}
      </div>
    </>
  );
}
